import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { useStateMachine } from "little-state-machine";

import "./InvoiceForm.scss";
import CardNew from "components/molecules/Card/CardNew";
import { useGetInvoiceConfig } from "api/queries/invoice/useGetInvoiceConfig";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Alert from "components/molecules/Alert/Alert";
import { Col, Row } from "react-bootstrap";
import { useGetInvoiceDraft } from "api/queries/invoice/useGetInvoiceDraft";
import OverlayLoader from "components/atoms/OverlayLoader/OverlayLoader";
import { saveInvoiceDraft, submitInvoice } from "api";
import { useGetInvoiceAttachments } from "api/queries/invoice/useGetInvoiceAttachments";
import { useAsyncDebounce } from "react-table";
import errorToast from "helpers/errorToast";
import { applyInvoiceSubmitServerErrors } from "helpers/invoiceSubmitServerErrors";
import { toast } from "react-toastify";
import { updateBackgroundLoadingState } from "store/store";
import { modifyDraftData, parseDraftValues } from "helpers/invoiceFormHelper";
import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";
import { invoiceQueryKeys } from "api/queries/invoice/invoiceQueryKeys";
import AuditLog from "pages/common/ShipmentBilling/components/AuditLog/AuditLog";
import Comments from "pages/common/ShipmentBilling/components/Comments/Comments";
import ShipmentBillingNav from "../ShipmentBillingNav/ShipmentBillingNav";
import ShipmentBillingActions from "../ShipmentBillingActions/ShipmentBillingActions";
import InvoiceFormGeneral from "./components/InvoiceFormGeneral/InvoiceFormGeneral";
import InvoiceFormGoods from "./components/InvoiceFormGoods/InvoiceFormGoods";
import InvoiceFormCosts from "./components/InvoiceFormCosts/InvoiceFormCosts";
import InvoiceFormAttachments from "./components/InvoiceFormAttachments/InvoiceFormAttachments";
import InvoiceFormRemarks from "./components/InvoiceFormRemarks/InvoiceFormRemarks";
import InvoiceFormConfirmation from "./components/InvoiceFormConfirmation/InvoiceFormConfirmation";
import findMissingMandatoryBuckets from "./invoiceUtils";

function InvoiceForm(props: any) {
  const { invoices, currentInvoice } = props;
  const isInvoiceAddressV2FeatureEnabled = useFeatureFlag(FEATURE_FLAGS.WEB_9731_INVOICE_ADDRESS_V2);
  const { id: shipmentId } = useParams();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [missingAttachmentBuckets, setMissingAttachmentBuckets] = useState<string[]>([]);
  const { actions } = useStateMachine({ updateBackgroundLoadingState });
  const submitBtnRef = useRef<HTMLButtonElement>(null);
  const saveInvoiceAbortController = useRef<AbortController | null>(null);
  const currentInvoiceId = searchParams.get("invoice");
  const { data: invoiceConfig, isLoading } = useGetInvoiceConfig({
    shipmentId: shipmentId!,
    currentInvoiceId: currentInvoiceId!,
    options: { refetchOnMount: false },
  });
  const { data: invoiceDraftData, isLoading: invoiceDraftLoading } = useGetInvoiceDraft({
    invoiceId: currentInvoiceId!,
  });
  const { data: attachmentsData, isLoading: invoiceAttachmentsLoading } = useGetInvoiceAttachments({
    invoiceId: currentInvoiceId!,
    options: { enabled: !!currentInvoiceId, refetchOnMount: false },
  });
  const methods = useForm<any>({ mode: "onChange" });
  const { handleSubmit, setError } = methods;
  const { invoice_instructions }: any = invoiceConfig || {};
  const { shipment }: any = invoiceDraftData || {};

  const isCustomsInvoice = currentInvoice?.invoice_type === "customs_broker_invoice";

  useEffect(() => {
    methods.reset(modifyDraftData(invoiceDraftData, invoiceConfig));
  }, [invoiceDraftData]);

  const handleSaveDraft = useAsyncDebounce(() => {
    // not saving because form has some errors
    // if (Object.keys(methods.formState.errors).length) {
    //   return;
    // }
    if (saveInvoiceAbortController.current) {
      saveInvoiceAbortController.current.abort();
    }

    saveInvoiceAbortController.current = new AbortController();
    const draftPayload = isInvoiceAddressV2FeatureEnabled ? parseDraftValues(methods.getValues()) : methods.getValues();
    return saveInvoiceDraft(currentInvoiceId, draftPayload, {
      signal: saveInvoiceAbortController.current.signal,
    })
      .then(() => {})
      .catch(() => {});
  }, 1000);

  const onSubmit = async () => {
    const missingMandatoryBuckets = findMissingMandatoryBuckets(
      attachmentsData,
      invoiceConfig,
      currentInvoice?.invoice_type,
    );

    if (!isCustomsInvoice && missingMandatoryBuckets.length > 0) {
      setMissingAttachmentBuckets(missingMandatoryBuckets);
      return;
    }

    setIsSubmitting(true);
    await handleSaveDraft();
    submitInvoice(currentInvoiceId)
      .then(() => {
        actions.updateBackgroundLoadingState(true);
        queryClient.invalidateQueries(invoiceQueryKeys.shipmentInvoicesListing(shipmentId!));
      })
      .catch((err) => {
        const costMessages = applyInvoiceSubmitServerErrors(err, setError);
        if (costMessages.length) {
          toast.error(
            <ul>
              {costMessages.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>,
          );
        }

        const { data } = err?.response ?? {};
        const otherErrorData =
          data && typeof data === "object" && !Array.isArray(data)
            ? Object.fromEntries(Object.entries(data).filter(([key]) => key !== "costs"))
            : null;
        const hasOtherFieldErrors = otherErrorData && Object.keys(otherErrorData).length > 0;

        if (hasOtherFieldErrors) {
          errorToast({ ...err, response: { ...err.response, data: otherErrorData } });
        } else if (!costMessages.length) {
          errorToast(err);
        }
      })
      .finally(() => setIsSubmitting(false));
  };

  const onError = () => {};

  const handleSubmitClick = () => {
    if (submitBtnRef.current) {
      submitBtnRef.current.click();
    }
  };

  return (
    <div className="w-100 position-relative" onBlur={handleSaveDraft}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit, onError)} className="invoice-form">
          <CardNew>
            <ShipmentBillingNav />
            <div className="mt-3">
              {isLoading && <LoadingSkeleton rows={2} noShadow isListing />}
              {!isLoading && !invoiceConfig && (
                <Alert className="mb-0" variant="danger">
                  <FormattedMessage
                    id="quote-request.couldNotRetrieveDetailsMsg"
                    defaultMessage="We could not retrieve any details. Please try again. If you continue to have trouble please contact admin."
                  />
                </Alert>
              )}
              {invoiceConfig && (
                <div>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex">
                      <h3>{currentInvoiceId}</h3>
                      {isCustomsInvoice && (
                        <Alert noIcon className="ml-xs" variant="warning">
                          <FormattedMessage
                            id="invoices.customBrokerageInvoice"
                            defaultMessage="Customs Brokerage Invoice"
                          />
                        </Alert>
                      )}
                    </div>
                    <ShipmentBillingActions />
                  </div>
                  {invoice_instructions?.length > 0 && (
                    <div className="invoice-wrapper mt-3">
                      <h6>
                        <FormattedMessage
                          id="invoices.invoiceInstruction"
                          values={{ shipper_name: shipment?.shipper_name }}
                          defaultMessage="Invoicing guidelines"
                        />
                      </h6>
                      <div className="mt-3">
                        {Array.isArray(invoice_instructions) ? (
                          invoice_instructions?.map((invoice: any, index: number) => (
                            <React.Fragment key={index}>
                              <h6 className="mt-1">
                                <FormattedMessage
                                  id="invoices.invoicingInstruction"
                                  defaultMessage="Invoicing instruction {number}"
                                  values={{ number: index + 1 }}
                                />
                              </h6>
                              <div>{invoice?.instruction}</div>
                            </React.Fragment>
                          ))
                        ) : (
                          <>{invoice_instructions}</>
                        )}
                      </div>
                    </div>
                  )}
                  <InvoiceFormGeneral
                    invoiceDraftData={invoiceDraftData}
                    invoiceConfig={invoiceConfig}
                    invoices={invoices}
                  />
                </div>
              )}
            </div>
          </CardNew>
          {invoiceConfig && (
            <>
              <CardNew className="mt-3">
                <InvoiceFormGoods />
              </CardNew>
              <CardNew className="mt-3">
                <InvoiceFormCosts invoiceConfig={invoiceConfig} />
              </CardNew>
              <Row className="mt-3">
                <Col md={6}>
                  <CardNew>
                    <InvoiceFormAttachments
                      isLoading={invoiceAttachmentsLoading}
                      missingAttachmentBuckets={missingAttachmentBuckets}
                    />
                  </CardNew>
                </Col>
                <Col md={6}>
                  <CardNew>
                    <InvoiceFormRemarks />
                  </CardNew>
                </Col>
              </Row>
              <CardNew className="mt-3">
                <InvoiceFormConfirmation onSubmit={handleSubmitClick} isLoading={isSubmitting} />
              </CardNew>
              <button ref={submitBtnRef} type="submit" className="form-submit">
                submit
              </button>
            </>
          )}
        </form>
      </FormProvider>
      {currentInvoice.invoice_status === "Declined" && (
        <Row className="mt-3">
          <Col md={6}>
            <CardNew>
              <AuditLog invoiceId={currentInvoice?.invoice_id} />
            </CardNew>
          </Col>
          <Col md={6}>
            <CardNew>
              <Comments invoiceId={currentInvoice?.invoice_id} />
            </CardNew>
          </Col>
        </Row>
      )}
      {invoiceDraftLoading && <OverlayLoader />}
    </div>
  );
}

export default InvoiceForm;
